import { Box, Dropdown, L80, L98 } from "@adfenix/alexandria";
import {
  Permissions,
  convertPermissionStringToObject,
  getUserPermissions,
  jwtPayload
} from "atoms/auth";
import { useEffect } from "react";
import { RoleDto, ScopeTypes } from "api/models/authServiceModels";
import {
  getDefaultPermissions,
  RoleWithPermissionsProps,
  userSelectedRoleAtom
} from "atoms/userSelectedRole";
import { useRecoilState, useRecoilValue } from "recoil";

import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";
import { getRoles } from "api/services/authService";
import { useLocation } from "react-router-dom";
import { useCheckPermission } from "hooks/usePermissions";

const SlideContainer = styled(Box)`
  position: fixed;
  top: 25%;
  right: -130px;
  border-radius: 6px 6px 0 0;
  z-index: 1010;
  overflow: hidden;
  background-color: ${({ theme }) => L98(theme.colors.grey)};
  border: 1px solid ${({ theme }) => L80(theme.colors.grey)};
  transform: rotate(-90deg);
  width: 300px;
  height: 40px;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const SelectRoleDropdown = () => {
  const { pathname } = useLocation();

  const payload = useRecoilValue(jwtPayload);
  const userPermissions = useRecoilValue(getUserPermissions);
  const hasAccessToRoleManagement = useCheckPermission({
    action: "View",
    scope: "RoleManagement"
  });

  const { data: roles } = useQuery(["getRoles"], getRoles, {
    staleTime: Infinity,
    enabled: payload?.accessLevel === "Multi-Brand" && hasAccessToRoleManagement
  });

  const [selectedRole, setSelectedRole] = useRecoilState(userSelectedRoleAtom);

  useEffect(() => {
    if (payload) {
      setSelectedRole({
        accessLevel: payload.accessLevel,
        name: payload.roleName,
        id: "",
        authenticationRequired: false,
        permissions: userPermissions ?? getDefaultPermissions()
      });
    }
  }, [payload, setSelectedRole, userPermissions]);

  const getRolePermissions = (role: RoleDto) => {
    return role.permissions
      .map(perm => perm.actions.map(action => `${perm.scope}:${action}`))
      .flat();
  };

  const onChange = (role: RoleDto) => {
    const newRole: RoleWithPermissionsProps = {
      ...role,
      permissions: convertPermissionStringToObject(
        role.permissions
          .map(p => `${p.actions.map(action => `${p.scope}:${action}`)}`)
          .toString()
      ) as Permissions
    };

    setSelectedRole(newRole);
  };

  const filteredRoles = () => {
    const currentUserPermissions =
      userPermissions &&
      Object.keys(userPermissions)
        .map(scope =>
          userPermissions[scope as ScopeTypes].map(
            action => `${scope}:${action}`
          )
        )
        .flat();

    const availableRoles = roles?.filter(role =>
      getRolePermissions(role).every(rp => currentUserPermissions?.includes(rp))
    );

    return (
      availableRoles?.slice(0).sort((a, b) => (a.name > b.name ? 0 : -1)) ?? []
    );
  };

  const rolesToDisplay = filteredRoles();

  return payload?.accessLevel === "Multi-Brand" &&
    rolesToDisplay.length > 0 &&
    !pathname.includes("sellers-choice-order-page") ? (
    <SlideContainer>
      <Dropdown
        style={{ border: "none" }}
        items={filteredRoles() ?? []}
        value={selectedRole?.name}
        onChange={role => onChange(role)}
        valueObjectIndexAccessor="name"
        placeholder="Role"
      />
    </SlideContainer>
  ) : null;
};
